import React, { useState, useEffect, useContext } from "react";
import PageLayout from "components/layouts/PageLayout";
import Notification from "lib/api/Notification";
import Client from "lib/api/Client";
import clientCache from "lib/utils/clientCache";
import ViewClientsModal from "components/notification/ViewClientsModal";
import {
  Button,
  TextInput,
  TextArea,
  Dropdown
} from "components/common";
import { formatEasternDateTime, formatEasternForDatetimeLocal, easternToUTC } from "lib/utils/dateUtils";
export default function NotificationForm(props) {
  const [notification, setNotification] = useState({
    source_uuid: "",
    type: "",
    title: "",
    message: "",
    start_at: "",
    end_at: "",
    created_at: "",
    updated_at: ""
  });
  const [notificationCount, setNotificationCount] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editStartAt, setEditStartAt] = useState("");
  const [editEndAt, setEditEndAt] = useState("");
  const [showClientsModal, setShowClientsModal] = useState(false);
  const [clientsWithNotifications, setClientsWithNotifications] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  async function load(source_uuid) {
    try{
      // Use the existing get endpoint with source_uuid filter
      const response = await Notification.get(0, 1, { source_uuid });
      if (response.status === 200 && response.data.length > 0) {
        const notif = response.data[0];
        await setNotification(notif);
        // Get the count from pagination.total
        await setNotificationCount(response.pagination?.total || 0);
        // Initialize edit fields with ET values
        setEditStartAt(formatEasternForDatetimeLocal(notif.start_at));
        setEditEndAt(formatEasternForDatetimeLocal(notif.end_at));
      }
    }catch(exception){
      console.log(exception);
    }
  }

  async function save() {
    try {
      const updatePayload = {
        type: notification.type,
        title: notification.title,
        message: notification.message,
        start_at: easternToUTC(editStartAt),
        end_at: easternToUTC(editEndAt)
      };
      const response = await Notification.updateBySource(notification.source_uuid, updatePayload);
      if (response.status === 200) {
        setIsEditing(false);
        // Reload to get the updated values from the server
        await load(notification.source_uuid);
        alert("Notification group updated successfully");
      } else {
        alert(`Failed to update notification group: ${response.message || 'Unknown error'}`);
      }
    } catch(exception) {
      console.log(exception);
      alert("Failed to update notification group");
    }
  }

  async function remove() {
    const confirmDelete = window.confirm(`Delete this notification?`);
    if (!confirmDelete) {
      return;
    }
    try {
      await Notification.deleteBySource(notification.source_uuid);
      props.history.push(`/notifications`);
    } catch(exception) {
      console.log(exception);
      alert("Failed to delete notification");
    }
  }

  async function handleClientDeleted() {
    // Reload the clients list after deletion
    await loadClientsData();
    // Also reload the notification count
    await load(notification.source_uuid);
  }

  async function handleClientAdded(clientUuid) {
    try {
      // Build notification object using current notification's properties
      const newNotification = {
        source_uuid: notification.source_uuid,
        target_uuid: clientUuid,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        start_at: notification.start_at,
        end_at: notification.end_at
      };

      // Create the notification
      const response = await Notification.create([newNotification]);

      if (response.status === 200 || response.status === 201) {
        // Reload the clients list to show the new client
        await loadClientsData();
        // Also reload the notification count
        await load(notification.source_uuid);
      } else {
        throw new Error(response.message || 'Unknown error');
      }
    } catch (exception) {
      console.error("Failed to add client:", exception);
      alert("Failed to add client to notification. Please try again");
    }
  }

  async function loadClientsData() {
    if (!notification.source_uuid) {
      return;
    }

    setLoadingClients(true);

    try {
      // Get all notifications with this source_uuid - only fetch uuid and target_uuid to reduce response size
      const response = await Notification.get(0, 10000, { source_uuid: notification.source_uuid, fields: 'uuid,target_uuid' });

      // Handle the response structure - it might be { data: [...], pagination: {...} } or { status: 200, data: [...] }
      const notificationsData = response.data || [];

      if (notificationsData.length > 0) {
        const notifications = notificationsData;

        // Extract unique target_uuids (client uuids)
        const uniqueClientUuids = [...new Set(notifications.map(n => n.target_uuid))].filter(uuid => uuid);

        // Create a map of target_uuid to notification_uuid
        const targetToNotificationMap = {};
        notifications.forEach(notif => {
          if (!targetToNotificationMap[notif.target_uuid]) {
            targetToNotificationMap[notif.target_uuid] = notif.uuid;
          }
        });

        if (uniqueClientUuids.length > 0) {
          // Step 1: Check cache first
          const cacheResult = clientCache.getMultiple(uniqueClientUuids);
          const cachedClients = Object.values(cacheResult.found);
          const uncachedUuids = cacheResult.missing;

          console.log(`Client cache: ${cachedClients.length} hits, ${uncachedUuids.length} misses`);

          // Step 2: Start with cached clients
          let clientsData = [...cachedClients];

          // Step 3: Fetch uncached clients from API (if any)
          if (uncachedUuids.length > 0) {
            if (uncachedUuids.length <= 100) {
              const clientsResponse = await Client.get({
                uuid: uncachedUuids.join(','),
                limit: uncachedUuids.length,
                fields: 'uuid,name,code'
              });
              const fetchedClients = clientsResponse?.data || [];

              // Cache the newly fetched clients
              if (fetchedClients.length > 0) {
                clientCache.setMultiple(fetchedClients);
                clientsData = [...clientsData, ...fetchedClients];
              }
            } else {
              // Batch requests for > 100 clients
              const batches = [];
              for (let i = 0; i < uncachedUuids.length; i += 100) {
                batches.push(uncachedUuids.slice(i, i + 100));
              }

              const batchPromises = batches.map(batch =>
                Client.get({
                  uuid: batch.join(','),
                  limit: batch.length,
                  fields: 'uuid,name,code'
                })
              );

              const batchResponses = await Promise.all(batchPromises);
              const fetchedClients = batchResponses.reduce((acc, response) => {
                return acc.concat(response?.data || []);
              }, []);

              // Cache the newly fetched clients
              if (fetchedClients.length > 0) {
                clientCache.setMultiple(fetchedClients);
                clientsData = [...clientsData, ...fetchedClients];
              }
            }
          }

          // Create a set of found client UUIDs
          const foundClientUuids = new Set(clientsData.map(c => c.uuid));

          // Merge client data with notification UUIDs
          const clientsWithNotifs = clientsData.map(client => ({
            ...client,
            target_uuid: client.uuid,
            notification_uuid: targetToNotificationMap[client.uuid]
          }));

          // Add notifications that don't have matching client data
          const unmatchedNotifications = uniqueClientUuids
            .filter(uuid => !foundClientUuids.has(uuid))
            .map(uuid => ({
              uuid: uuid,
              name: null,
              code: null,
              target_uuid: uuid,
              notification_uuid: targetToNotificationMap[uuid]
            }));

          const allRecords = [...clientsWithNotifs, ...unmatchedNotifications];

          // Sort: clients with names first (alphabetically), then unmatched by UUID
          allRecords.sort((a, b) => {
            if (a.name && b.name) {
              return a.name.localeCompare(b.name);
            }
            if (a.name && !b.name) return -1;
            if (!a.name && b.name) return 1;
            return (a.target_uuid || '').localeCompare(b.target_uuid || '');
          });

          setClientsWithNotifications(allRecords);
        } else {
          setClientsWithNotifications([]);
        }
      } else {
        setClientsWithNotifications([]);
      }
    } catch(exception) {
      console.log(exception);
      alert("Failed to load clients");
      setClientsWithNotifications([]);
    } finally {
      setLoadingClients(false);
    }
  }

  async function loadClients() {
    setShowClientsModal(true);
    await loadClientsData();
  }

  useEffect(() => {
      if(props.match.params.uuid){
        load(props.match.params.uuid);
      }
  }, []);

  const deleteBtn = props.match.params.uuid ? (
    <Button className="button button--danger" onClick={remove}>
      Delete
    </Button>
  ) : (
    ""
  );
  return (
    <PageLayout>
      <h1>Notification Details</h1>
      <div className="hr hr--darkest"></div>
      <div className="row">
        <div className="col-xs-12 col-sm-4">
          <Dropdown
            label="Type"
            value={notification.type}
            disabled={!isEditing}
            onChange={e => setNotification({...notification, type: e.target.value})}
          >
            <option value="upcoming">Upcoming</option>
            <option value="learning">Learning</option>
            <option value="release">Release</option>
            <option value="system">System</option>
            <option value="unknown">Unknown</option>
            <option value="marketing">Marketing</option>
            <option value="billing">Billing</option>
          </Dropdown>
        </div>
        <div className="col-xs-12 col-sm-4">
          <TextInput
            className="m-bot-0"
            label="Title"
            placeholder="Enter Title"
            type="text"
            value={notification.title}
            disabled={!isEditing}
            onChange={e => setNotification({...notification, title: e.target.value})}
          />
        </div>
        <div className="col-xs-12 col-sm-4">
          <TextInput
            className="m-bot-0"
            label="Notification Count"
            placeholder="Count"
            type="number"
            value={notificationCount}
            disabled={true}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <TextArea
            label="Message"
            placeholder="Enter Message"
            value={notification.message}
            disabled={!isEditing}
            onChange={e => setNotification({...notification, message: e.target.value})}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <label>
            <span className="label__span">Start (Eastern Time)</span>
            <input type="datetime-local"
              value={editStartAt}
              onChange={e => setEditStartAt(e.target.value)}
              disabled={!isEditing}
            />
          </label>
        </div>
        <div className="col-xs-12 col-sm-6">
          <label>
            <span className="label__span">End (Eastern Time)</span>
            <input type="datetime-local"
              value={editEndAt}
              onChange={e => setEditEndAt(e.target.value)}
              disabled={!isEditing}
            />
          </label>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <TextInput
            className="m-bot-0"
            label="Created At"
            type="text"
            value={formatEasternDateTime(notification.created_at)}
            disabled={true}
          />
        </div>
        <div className="col-xs-12 col-sm-6">
          <TextInput
            className="m-bot-0"
            label="Updated At"
            type="text"
            value={formatEasternDateTime(notification.updated_at)}
            disabled={true}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <TextInput
            className="m-bot-0"
            label="Source UUID"
            placeholder="Source UUID"
            type="text"
            value={notification.source_uuid}
            disabled={true}
            onChange={e => setNotification({...notification, source_uuid: e.target.value})}
          />
        </div>
      </div>
      {notification.source_uuid && (
        <div className="row m-top-20">
          <div className="col-xs-12">
            <Button className="button button--plain" onClick={loadClients}>
              View Clients
            </Button>
          </div>
        </div>
      )}
      <div className="hr hr--darkest m-top-10 m-bot-20"></div>
      <div className="row">
        <div className="col-xs-6">
          <div className="button-group">
            {!isEditing ? (
              <Button className="button" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            ) : (
              <>
                <Button className="button" onClick={save}>
                  Save
                </Button>
                <Button className="button button--plain" onClick={() => {
                  setIsEditing(false);
                  // Reset edit fields to original values
                  setEditStartAt(formatEasternForDatetimeLocal(notification.start_at));
                  setEditEndAt(formatEasternForDatetimeLocal(notification.end_at));
                }}>
                  Cancel Edit
                </Button>
              </>
            )}
            <a className="button button--plain" href="#" onClick={props.history.goBack}>
              Back
            </a>
          </div>
        </div>
        <div className="col-xs-6 end-xs">
          <div className="button-group">
            {deleteBtn}
          </div>
        </div>
      </div>

      <ViewClientsModal
        show={showClientsModal}
        onClose={() => setShowClientsModal(false)}
        clientsWithNotifications={clientsWithNotifications}
        loading={loadingClients}
        onClientDeleted={handleClientDeleted}
        notification={notification}
        onClientAdded={handleClientAdded}
      />
    </PageLayout>
  );
}
